import { useCallback, useRef, useState } from "react"

import type { ConversationStartResponse } from "@/types/conversation"
import { getJson, postJson } from "@/utils/api"

export function useConversation() {
  const [conversation, setConversation] =
    useState<ConversationStartResponse | null>(null)
  const startingRef = useRef(false)

  const startConversation = useCallback(
    async (
      configOverride?: Record<string, unknown>,
    ): Promise<ConversationStartResponse | null> => {
      if (startingRef.current || conversation) return conversation
      startingRef.current = true
      try {
        const resp = await postJson<ConversationStartResponse>(
          "/api/conversations",
          {
            session_type: "anonymous",
            // npc_type (the Score) is chosen later from the user's focus, via
            // POST /api/conversations/:id/start — not at creation time.
            // configOverride threads Karaoke mode through: {mode, script_id}.
            configuration: {
              pace_preset: "default",
              ...(configOverride ?? {}),
            },
            user_context: {},
            client_meta: { client_ts: Date.now() },
          },
        )
        setConversation(resp)
        return resp
      } finally {
        startingRef.current = false
      }
    },
    [conversation],
  )

  // Rejoin an existing conversation (admin "jump in" / pick up where it left
  // off). Fetches the same envelope #create returns; the events hook then
  // streams the existing history back. Does not create anything server-side.
  const resumeConversation = useCallback(
    async (id: number): Promise<ConversationStartResponse | null> => {
      if (startingRef.current) return conversation
      startingRef.current = true
      try {
        const resp = await getJson<ConversationStartResponse>(
          `/api/conversations/${id}`,
        )
        setConversation(resp)
        return resp
      } finally {
        startingRef.current = false
      }
    },
    [conversation],
  )

  const resetConversation = useCallback(() => {
    setConversation(null)
  }, [])

  return {
    conversation,
    startConversation,
    resumeConversation,
    resetConversation,
  }
}
